import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TestConfigWizard } from './TestConfigWizard';
import { ComparisonGrid } from './ComparisonGrid';
import { useABTestingSessions } from '@/hooks/useABTestingSessions';
import { useABTestVariants } from '@/hooks/useABTestVariants';
import { useImageQueue } from '@/hooks/useImageQueue';
import { FlaskConical, ArrowLeft, Loader2, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';

type ActiveStep = 'setup' | 'generating' | 'comparing' | 'results';

interface ABTestingLabProps {
  existingSessionId?: string | null;
}

export const ABTestingLab = ({ existingSessionId }: ABTestingLabProps) => {
  const [activeStep, setActiveStep] = useState<ActiveStep>(existingSessionId ? 'comparing' : 'setup');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(existingSessionId || null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  
  const { createSession, sessions, togglePublicAccess } = useABTestingSessions();
  const { user } = useSecureAuthContext();
  const { variants, loading: variantsLoading, castVote, setWinner, createVariant, deleteVariant } = useABTestVariants(currentSessionId);
  const { addToQueue } = useImageQueue(user?.id);

  const currentSession = sessions.find(s => s.id === currentSessionId);

  const handleTestComplete = async (config: any) => {
    try {
      console.log('🧪 Starting A/B test creation:', config);
      setActiveStep('generating');

      // Create test session
      console.log('📝 Creating session...');
      const { data: session, error: sessionError } = await createSession({
        name: config.name,
        description: config.description,
        base_prompt: config.basePrompt,
        test_type: config.testType,
        status: 'active',
        winner_variant_id: null,
        completed_at: null,
        metadata: { variants_count: config.variants.length }
      });

      if (sessionError) {
        console.error('❌ Session creation error:', sessionError);
        throw new Error(sessionError.message || 'Failed to create test session');
      }

      if (!session) {
        console.error('❌ No session returned');
        throw new Error('Failed to create test session - no data returned');
      }

      console.log('✅ Session created:', session.id);
      setCurrentSessionId(session.id);

      // Generate images for each variant
      console.log('🖼️ Starting image generation for', config.variants.length, 'variants');
      
      for (let index = 0; index < config.variants.length; index++) {
        const variant = config.variants[index];
        try {
          const variantPrompt = variant.style
            ? `${config.basePrompt}, ${variant.style}`
            : config.basePrompt;

          console.log(`🎨 Adding variant ${index + 1} to queue:`, variant);

          // Create DB variant record so it appears in the grid immediately
          const { data: createdVariant, error: variantCreateError } = await createVariant({
            session_id: session.id,
            image_id: null,
            variant_name: variant.name || `Variant ${index + 1}`,
            variant_config: {
              base_prompt: config.basePrompt,
              ...variant
            },
            internal_notes: null
          } as any);

          if (variantCreateError) {
            console.error('❌ Failed to create variant record:', variantCreateError);
          } else {
            console.log('✅ Variant record created:', createdVariant?.id);
          }

          // Add to generation queue with proper typing
          const queueId = await addToQueue({
            prompt: variantPrompt,
            model: variant.model || 'provider-3/gpt-image-1',
            numberOfImages: 1,
            size: variant.size || '1024x1024',
            quality: variant.quality || 'standard',
            style: variant.style || 'natural',
            generation_type: 'single' as const,
            metadata: { abTestVariantId: createdVariant?.id }
          });
          
          if (!queueId) {
            throw new Error('Queue add failed — please ensure you are signed in.');
          }
          
          toast.success(`Variant ${index + 1} added to generation queue`);
        } catch (variantError) {
          console.error(`❌ Error adding variant ${index + 1}:`, variantError);
          toast.error(`Failed to add variant ${index + 1}`);
        }
      }

      toast.success('All variants added to generation queue!');
      console.log('✅ A/B test setup complete');
      setActiveStep('comparing');
    } catch (error) {
      console.error('❌ Error creating test:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create test');
      setActiveStep('setup');
    }
  };

  const handleVote = async (variantId: string) => {
    const result = await castVote(variantId);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Vote recorded!');
    }
  };

  const handleSetWinner = async (variantId: string) => {
    const result = await setWinner(variantId);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Winner selected! 🎉', {
        description: 'Test marked as completed'
      });
      setActiveStep('results');
    }
  };

  const handleBack = () => {
    setCurrentSessionId(null);
    setActiveStep('setup');
  };

  const handleTogglePublic = async () => {
    if (!currentSessionId) return;
    
    const newPublicState = !currentSession?.is_public;
    const result = await togglePublicAccess(currentSessionId, newPublicState);
    
    if (!result.error) {
      toast.success(newPublicState ? 'Test is now public and shareable!' : 'Test is now private');
    } else {
      toast.error(result.error);
    }
  };

  const handleCopyShareLink = () => {
    if (!currentSession?.share_token) return;
    
    const shareUrl = `${window.location.origin}/ab-testing/shared/${currentSession.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied to clipboard!');
  };

  const handleDeleteVariant = async (variantId: string) => {
    try {
      const result = await deleteVariant(variantId);
      if (result.error) {
        throw new Error(result.error);
      }
      // Success toast is handled in ComparisonGrid
    } catch (error) {
      toast.error('Failed to delete variant');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {activeStep !== 'setup' && (
            <Button
              variant="subtle"
              size="sm"
              onClick={handleBack}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <FlaskConical className="w-8 h-8 mr-3 text-blue-400" />
              A/B Testing Lab
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Compare different creative directions systematically
            </p>
          </div>
        </div>
      </div>

      {/* Content based on active step */}
      <Card className="bg-white/5 border-white/10 p-6">
        {activeStep === 'setup' && (
          <TestConfigWizard
            onComplete={handleTestComplete}
            onCancel={() => {
              // Handle cancel if needed
            }}
          />
        )}

        {activeStep === 'generating' && (
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
            <h3 className="text-white text-xl font-semibold mb-2">
              Generating Variants...
            </h3>
            <p className="text-gray-400">
              Your images are being generated. This may take a few minutes.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Check the Queue Monitor tab for detailed progress
            </p>
            <Button
              variant="default"
              onClick={() => setActiveStep('comparing')}
              className="mt-6"
            >
              View Comparison Grid
            </Button>
          </div>
        )}

        {(activeStep === 'comparing' || activeStep === 'results') && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">
                {activeStep === 'results' ? 'Test Results' : 'Compare Variants'}
              </h2>
              <div className="flex items-center gap-3">
                {currentSession && (
                  <>
                    <Button
                      size="sm"
                      variant={currentSession.is_public ? 'default' : 'outline'}
                      onClick={handleTogglePublic}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      {currentSession.is_public ? 'Public' : 'Make Public'}
                    </Button>
                    {currentSession.is_public && currentSession.share_token && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleCopyShareLink}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                    )}
                  </>
                )}
                {variants.length > 0 && (
                  <div className="text-sm text-gray-400">
                    {variants.length} variants
                  </div>
                )}
              </div>
            </div>

            {variantsLoading ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading variants...</p>
              </div>
            ) : (
              <ComparisonGrid
                variants={variants}
                onVote={handleVote}
                onSetWinner={handleSetWinner}
                onDeleteVariant={handleDeleteVariant}
                isLoading={false}
              />
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
